import { Box } from '@mui/system';
import { SlColumn, SlRow, useStacklineTheme } from '@stackline/ui';
import React, { useMemo, useState } from 'react';
import SegmentCardGrid from 'src/components/BeaconV2Components/ExperimentalLayout/Segments/SegmentCardGrid';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { SearchTextField } from 'src/components/BeaconV2Components/SearchInput/SearchInputBar';
import { SlClickThruAction } from 'src/components/BeaconV2Components/SlClickThruAction';
import SlPlaceholderMessage from 'src/components/BeaconV2Components/SlMessagePlaceholder/SlMessagePlaceholder';
import { NoDataIcon } from 'src/components/BeaconV2Components/common/NoDataPlaceHolder/NoDataPlaceHolder';
import { SlTripleFolderIcon } from 'src/components/SvgIcons';
import { useDebouncedValue, useAppSelector, useHistory } from 'src/utils/Hooks';

interface TopicsViewTabProps {
  /**
   * True if we are viewing team topics
   */
  team?: boolean;
}

const TopicsViewTab = ({ team }: TopicsViewTabProps) => {
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search);

  const theme = useStacklineTheme();
  const myTopics = useAppSelector((state) => state.segments.mySearchTermLists);
  const teamTopics = useAppSelector((state) => state.segments.teamSearchTermLists);
  const topics = useMemo(() => (team ? teamTopics : myTopics), [myTopics, team, teamTopics]);
  const history = useHistory();
  const { searchParams } = useAppSelector((state) => state.app.queryParams);

  const filteredTopics = useMemo(() => {
    return topics.filter((topic) => {
      const displayMatches = topic.displayName.toLowerCase().includes(debouncedSearch.toLowerCase());

      const owner = `${topic.segment.owner.firstName} ${topic.segment.owner.lastName}`;
      const ownerMatches = owner.toLowerCase().includes(debouncedSearch.toLowerCase());

      return displayMatches || ownerMatches;
    });
  }, [debouncedSearch, topics]);

  const handleCreateTopicClick = () => {
    history.push(`/search${searchParams}&entityType=searchtermlist&action=create`);
  };

  return (
    <SlColumn widths="full">
      <SearchTextField
        placeholder="Search topics"
        value={search}
        onChange={(event) => {
          const { value } = event.target;
          setSearch(value);
        }}
      />
      <SlRow horizontalPosition="center">
        {filteredTopics.length < 1 && (
          <Flex gap="sm" flexDirection="column" marginTop={theme.spacing.xxl}>
            <SlPlaceholderMessage
              renderIcon={() => {
                return (
                  <NoDataIcon
                    icon={<SlTripleFolderIcon />}
                    iconBackgroundStyle={{
                      backgroundColor: theme.colors.secondaryGhost
                    }}
                  />
                );
              }}
              title="Add topics"
              subtitle="There are currently no topics to display. All of your topics will be available on this page."
            />
            <SlClickThruAction textSx={{ fontSize: '16px' }} onClick={handleCreateTopicClick} label="Create a topic" />
          </Flex>
        )}
      </SlRow>
      {filteredTopics.length > 0 && (
        <Box marginTop={theme.spacing.lg} width="100%">
          <SegmentCardGrid
            segments={filteredTopics}
            indexName="traffic"
            fieldName="totalClicks"
            metricDisplayName="Total Traffic"
            color="accentMagenta"
            isTopicSegment
          />
        </Box>
      )}
    </SlColumn>
  );
};

export default TopicsViewTab;
